import { useState, useEffect } from "react";
import { X, Share, Plus, MoreVertical, Download } from "lucide-react";

export const InstallBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [platform, setPlatform] = useState("other");
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const dismissed = sessionStorage.getItem("pwa-dismissed");
    if (dismissed) return;

    const ua = navigator.userAgent;
    const standalone = window.navigator.standalone === true || window.matchMedia("(display-mode: standalone)").matches;
    if (standalone) return;

    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isAndroid = /Android/.test(ua);
    const isSamsung = /SamsungBrowser/.test(ua);
    const isSafariDesktop = /^((?!chrome|android).)*safari/i.test(ua) && !isIOS;

    if (isIOS) {
      const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS/.test(ua);
      setPlatform(isSafari ? "ios-safari" : "ios-other");
      setShowBanner(true);
      return;
    }

    if (isSamsung) {
      setPlatform("samsung");
      setShowBanner(true);
      return;
    }

    if (isAndroid) {
      setPlatform("android");
    } else if (isSafariDesktop) {
      setPlatform("mac-safari");
      setShowBanner(true);
      return;
    } else {
      setPlatform("desktop");
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") setShowBanner(false);
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    sessionStorage.setItem("pwa-dismissed", "1");
  };

  if (!showBanner) return null;

  const hasNativeInstall = deferredPrompt && (platform === "android" || platform === "desktop");

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-[#EBE3DF] shadow-[0_-4px_24px_rgba(0,0,0,0.08)]"
      data-testid="install-banner"
    >
      <div className="max-w-lg mx-auto p-4 pb-[env(safe-area-inset-bottom,16px)]">
        <div className="flex items-center gap-3">
          <img src="/icon-192.png" alt="" className="w-11 h-11 rounded-xl flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-medium text-[#111111] text-sm">Lägg till Emotionell Frihet</p>
            <p className="text-xs text-[#111111]/40">Få appen på din hemskärm</p>
          </div>

          {hasNativeInstall ? (
            <button
              onClick={handleInstall}
              className="flex-shrink-0 bg-[#A0655A] text-white text-xs font-medium px-4 py-2 rounded-full hover:bg-[#8B5549] transition-colors"
              data-testid="install-btn"
            >
              Installera
            </button>
          ) : (
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex-shrink-0 bg-[#A0655A] text-white text-xs font-medium px-4 py-2 rounded-full hover:bg-[#8B5549] transition-colors"
              data-testid="show-instructions-btn"
            >
              Visa hur
            </button>
          )}

          <button
            onClick={handleDismiss}
            className="flex-shrink-0 p-1.5 text-[#111111]/30 hover:text-[#111111]/60 transition-colors"
            data-testid="dismiss-install-btn"
          >
            <X size={16} />
          </button>
        </div>

        {expanded && (
          <div className="mt-4 pt-4 border-t border-[#EBE3DF]/60">
            {platform === "ios-safari" && (
              <div className="space-y-3" data-testid="ios-safari-instructions">
                <p className="text-xs font-medium text-[#111111]/70 mb-2">Så här gör du på iPhone/iPad:</p>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#A0655A] text-white text-[10px] flex items-center justify-center font-medium">1</span>
                  <p className="text-xs text-[#111111]/60">Tryck på <Share size={13} className="inline text-[#A0655A] -mt-0.5" /> <strong>Dela-knappen</strong> längst ner i Safari</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#A0655A] text-white text-[10px] flex items-center justify-center font-medium">2</span>
                  <p className="text-xs text-[#111111]/60">Scrolla ner och tryck <Plus size={13} className="inline text-[#A0655A] -mt-0.5" /> <strong>"Lägg till på hemskärmen"</strong></p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#A0655A] text-white text-[10px] flex items-center justify-center font-medium">3</span>
                  <p className="text-xs text-[#111111]/60">Tryck <strong>"Lägg till"</strong> uppe till höger</p>
                </div>
              </div>
            )}

            {platform === "ios-other" && (
              <div data-testid="ios-other-instructions">
                <p className="text-xs font-medium text-[#111111]/70 mb-2">Öppna i Safari först:</p>
                <p className="text-xs text-[#111111]/60 leading-relaxed">
                  Kopiera länken <strong className="text-[#A0655A]">emotionellfrihet.se</strong> och öppna den i <strong>Safari</strong>. 
                  Tryck sedan på <Share size={13} className="inline text-[#A0655A] -mt-0.5" /> och välj <strong>"Lägg till på hemskärmen"</strong>.
                </p>
              </div>
            )}

            {platform === "samsung" && (
              <div className="space-y-3" data-testid="samsung-instructions">
                <p className="text-xs font-medium text-[#111111]/70 mb-2">Så här gör du i Samsung Internet:</p>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#A0655A] text-white text-[10px] flex items-center justify-center font-medium">1</span>
                  <p className="text-xs text-[#111111]/60">Tryck på <strong>menyknappen</strong> (tre horisontella linjer) längst ner</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#A0655A] text-white text-[10px] flex items-center justify-center font-medium">2</span>
                  <p className="text-xs text-[#111111]/60">Tryck <Plus size={13} className="inline text-[#A0655A] -mt-0.5" /> <strong>"Lägg till sida i"</strong> och sedan <strong>"Hemskärmen"</strong></p>
                </div>
              </div>
            )}

            {platform === "mac-safari" && (
              <div className="space-y-3" data-testid="mac-safari-instructions">
                <p className="text-xs font-medium text-[#111111]/70 mb-2">Så här gör du i Safari på Mac:</p>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#A0655A] text-white text-[10px] flex items-center justify-center font-medium">1</span>
                  <p className="text-xs text-[#111111]/60">Klicka på <strong>Arkiv</strong> i menyraden</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#A0655A] text-white text-[10px] flex items-center justify-center font-medium">2</span>
                  <p className="text-xs text-[#111111]/60">Välj <strong>"Lägg till i Dock"</strong></p>
                </div>
              </div>
            )}

            {platform === "android" && !deferredPrompt && (
              <div className="space-y-3" data-testid="android-instructions">
                <p className="text-xs font-medium text-[#111111]/70 mb-2">Så här gör du i Chrome:</p>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#A0655A] text-white text-[10px] flex items-center justify-center font-medium">1</span>
                  <p className="text-xs text-[#111111]/60">Tryck på <MoreVertical size={13} className="inline text-[#A0655A] -mt-0.5" /> <strong>tre prickar</strong> uppe till höger</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#A0655A] text-white text-[10px] flex items-center justify-center font-medium">2</span>
                  <p className="text-xs text-[#111111]/60">Tryck <Download size={13} className="inline text-[#A0655A] -mt-0.5" /> <strong>"Installera app"</strong> eller <strong>"Lägg till på hemskärmen"</strong></p>
                </div>
              </div>
            )}

            {platform === "desktop" && !deferredPrompt && (
              <div data-testid="desktop-instructions">
                <p className="text-xs font-medium text-[#111111]/70 mb-2">Så här gör du i Chrome/Edge:</p>
                <p className="text-xs text-[#111111]/60 leading-relaxed">
                  Klicka på <Download size={13} className="inline text-[#A0655A] -mt-0.5" /> <strong>installationsikonen</strong> i adressfältet (till höger om URL:en) och välj <strong>"Installera"</strong>.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
